import { User } from "lucide-react";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import type { ProgressSectionId } from "../lib/progress-calculator";

interface IntroScreenProps {
  onContinue: () => void;
  onBack?: () => void;
  onClose?: () => void;
  language?: "en" | "es";
  logoUrl?: string;
  logoUrlAlt?: string;
  currentStep?: number;
  totalSteps?: number;
  sectionIndex?: number;
  totalSections?: number;
  sectionId?: ProgressSectionId;
}

export function IntroScreen({
  onContinue,
  onBack,
  onClose,
  language = "en",
  logoUrl,
  logoUrlAlt,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
}: IntroScreenProps) {
  const t = getNamespaceTranslations(language, "intro");

  return (
    <div className="w-full min-h-screen flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
      <Header logoUrl={logoUrl} logoAlt={logoUrlAlt} onBack={onBack} onClose={onClose} />

      <main className="flex-1 w-full flex items-start justify-center py-[30px] px-5 lg:px-4 overflow-y-auto">
        <div className="w-full max-w-[640px]">
          {currentStep !== undefined &&
            totalSteps !== undefined &&
            sectionIndex !== undefined &&
            totalSections !== undefined &&
            sectionId !== undefined && (
            <ProgressBar
              currentStep={currentStep}
              totalSteps={totalSteps}
              sectionIndex={sectionIndex}
              totalSections={totalSections}
              sectionId={sectionId}
              language={language}
            />
          )}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 mt-8">
            <div className="flex gap-4 flex-row items-center">
              <User className="w-6 h-6 text-[#0B2545] mb-4" aria-hidden="true" />

              <h1
                className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal text-center mb-4"
                style={{ fontFamily: "TT Commons Pro" }}
              >
                {t?.title}
              </h1>
            </div>

            <p
              className="text-base leading-[22px] text-gray-700 mb-4"
              style={{ fontFamily: "TT Commons Pro" }}
            >
              {t?.description}
            </p>

            <ul className="space-y-3 mb-6 list-disc pl-5">
              <li className="text-base leading-[22px] text-gray-700" style={{ fontFamily: "TT Commons Pro" }}>
                <span className="font-semibold text-black">{t?.bulletFastTitle}</span>
              </li>
              <li className="text-base leading-[22px] text-gray-700" style={{ fontFamily: "TT Commons Pro" }}>
                <span className="font-semibold text-black">{t?.bulletPrivateTitle}</span> {t?.bulletPrivateBody}
              </li>
              <li className="text-base leading-[22px] text-gray-700" style={{ fontFamily: "TT Commons Pro" }}>
                <span className="font-semibold text-black">{t?.bulletNoCommitmentTitle}</span> {t?.bulletNoCommitmentBody}
              </li>
            </ul>

            <div className="flex items-start gap-3 bg-[#F5F1EC] rounded-xl p-4 mb-6">
              <div
                className="w-9 h-9 rounded-full bg-[#0B2545] text-white flex items-center justify-center text-xs font-semibold flex-shrink-0"
                aria-hidden="true"
              >
                MD
              </div>
              <p className="text-sm leading-[20px] text-gray-700" style={{ fontFamily: "TT Commons Pro" }}>
                {t?.clinicalReviewNote}
              </p>
            </div>

            <button
              type="button"
              onClick={onContinue}
              className="w-full h-[50px] inline-flex items-center justify-center rounded-full bg-[#2D55FB] text-white px-[30px] text-lg leading-6 font-medium transition-colors hover:bg-[#2347E8]"
              style={{ fontFamily: "TT Commons Pro" }}
              data-testid="intro-continue_button"
            >
              {t?.continue}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
